import { useEffect, useState } from "react";

import useLeadsOnCollectionDay from "../../data/useLeadsOnCollectionDays";
import useTrips from "../../data/useTrips";
import useTrucks from "../../data/useTrucks";
import { ILead, LeadStatus } from "../../models/Lead";
import { ICalendarTrip } from "../../models/Trip";
import { PartsStatusValues } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import { getLeadPostcode, getLeadTown } from "../../utils/getLeadLocation";
import { DateTime } from "luxon";
import LoadingWheel from "../shared/LoadingWheel";

const THIRD_PARTY_TITLE = "Third Party";

interface IJobSheetSection {
  title: string;
  leads: ILead[];
}

export default function JobSheetTable({
  selectedDay,
  week,
  year,
}: {
  selectedDay: string | undefined;
  week: number;
  year: number;
}) {
  const { leadsOnCollectionDay } = useLeadsOnCollectionDay(
    selectedDay,
    "JobSheetTable"
  );
  const { trucks } = useTrucks();
  const { trips } = useTrips(year, week);

  const [todaysLeads, setTodaysLeads] = useState<ILead[]>();
  const [todaysTrips, setTodaysTrips] = useState<ICalendarTrip[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (
      selectedDay &&
      !leadsOnCollectionDay.isLoading &&
      leadsOnCollectionDay.data &&
      !trips.isLoading &&
      trips.data &&
      !trucks.isLoading &&
      trucks.data
    ) {
      const selectedDate = DateTime.fromISO(selectedDay).toISODate();

      setTodaysLeads(
        leadsOnCollectionDay.data.data.leads!.filter(
          (lead) => lead.status !== LeadStatus.Cancelled
        )
      );

      setTodaysTrips(
        trips.data.data.find(
          (trip) => DateTime.fromISO(trip.date).toISODate() === selectedDate
        )?.trips
      );

      setIsLoading(false);
    }
  }, [
    leadsOnCollectionDay.isLoading,
    leadsOnCollectionDay.data,
    trips.isLoading,
    trips.data,
    trucks.isLoading,
    trucks.data,
    selectedDay,
  ]);

  const handleBackground = (status: PartsStatusValues) => {
    switch (status) {
      case PartsStatusValues["Awaiting Schedule"]:
        return "awaiting";
      case PartsStatusValues["Export - Engine + Frontend"]:
      case PartsStatusValues["Export - Engine"]:
      case PartsStatusValues["Export - Frontend"]:
        return "export";
      case PartsStatusValues["Parts - No Engine"]:
      case PartsStatusValues["Parts + Export"]:
      case PartsStatusValues["Parts - Engine + Gearbox"]:
        return "sales";
      case PartsStatusValues["Scrap"]:
        return "scrap";
      case PartsStatusValues["Salvage"]:
        return "salvage";
      case PartsStatusValues["Cancelled"]:
        return "cancelled";
      default:
        return "";
    }
  };

  const leadsForTrip = (trip: ICalendarTrip) =>
    (todaysLeads?.filter((lead) => trip.leads.includes(lead.id)) ?? []).sort(
      (a, b) => (a.order > b.order ? 1 : -1)
    );

  const buildSections = (): IJobSheetSection[] => {
    const truckList = trucks.data?.data ?? [];
    const trucksById = new Set(truckList.map((t) => t.id));
    const allTrips = todaysTrips ?? [];
    const sections: IJobSheetSection[] = [];

    truckList.forEach((truck) => {
      const leads = allTrips
        .filter((trip) => trip.truckId === truck.id)
        .flatMap((trip) => leadsForTrip(trip));

      if (leads.length > 0) {
        sections.push({ title: truck.name, leads });
      }
    });

    const thirdPartyLeads = allTrips
      .filter((trip) => !trucksById.has(trip.truckId))
      .flatMap((trip) => leadsForTrip(trip));

    if (thirdPartyLeads.length > 0) {
      sections.push({ title: THIRD_PARTY_TITLE, leads: thirdPartyLeads });
    }

    return sections;
  };

  if (isLoading) {
    return <LoadingWheel />;
  }

  const sections = buildSections();

  return (
    <>
      {sections.map((section) => (
        <div className="truck-section" key={section.title}>
          <table className="print-table">
            <thead>
              <tr>
                <th
                  colSpan={8}
                  className="truck"
                  style={{
                    textAlign: "center",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  {section.title}
                </th>
              </tr>
              <tr>
                <th>Reg</th>
                <th>Make/Model</th>
                <th>Location</th>
                <th>Postcode</th>
                <th>Driver Notes</th>
                <th>Vehicle Notes</th>
                <th>Engine Code</th>
                <th>Parts/Scrap</th>
              </tr>
            </thead>
            <tbody>
              {section.leads.map((lead) => (
                <tr
                  key={lead.id}
                  className={classNames(
                    handleBackground(lead.vehicle?.engineExportStatus!),
                    ""
                  )}
                  style={{ fontSize: "14px" }}
                >
                  <td>{lead.vehicle?.vrm}</td>
                  <td style={{ fontSize: "12px" }}>
                    {lead.vehicle?.combinedMake} {lead.vehicle?.combinedModel}
                  </td>
                  <td style={{ fontSize: "12px" }}>{getLeadTown(lead)}</td>
                  <td>{getLeadPostcode(lead)}</td>
                  <td style={{ whiteSpace: "pre-line" }}>
                    {lead.driverNotes}
                  </td>
                  <td style={{ whiteSpace: "pre-line" }}>
                    {lead.vehicle?.notes}
                  </td>
                  <td style={{ fontSize: "12px" }}>
                    {lead.vehicle?.engineModelCode}
                  </td>
                  <td style={{ fontSize: "12px" }}>
                    {PartsStatusValues[
                      lead.vehicle?.engineExportStatus!
                    ]?.toString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}
